import { ViewType } from "../App";
import { profiles } from "../profiles";
import { getConcessionSelected, getContext } from "./sessionStorage";

/**
 * YOS-9865 — the superadmin's "view as" mode and the hidden concessions.
 *
 * A concession can be flagged with the `omit_from_venue_reports` metadata, and the API hides it from the
 * reports of the profiles that must not see it, the superadmin included. When the superadmin uses the view
 * mode to work as a concession's supervisor they need to see exactly that concession, so the panel declares
 * it with these headers and the API waives THAT concession only, and only for report reads.
 *
 * They are optional: any client that does not send them (POS, app, ATMs) behaves exactly as before.
 * They are headers and NOT query params: 134 API handlers read their filters as
 * `hasValue(queryStringParameters) ? queryStringParameters : JSON.parse(body)`, so adding any query param
 * to a POST makes them read an empty query bag instead of the body and answer 400.
 * The API validates the profile against the session token, so sending them as another profile grants nothing.
 */
export const VIEW_AS_HEADER = "x-yoshi-view-as";
export const VIEW_AS_CONCESSION_HEADER = "x-yoshi-view-as-concession";

/**
 * Endpoint prefixes the signal is sent to. An allowlist on purpose, not "everything": every service that
 * receives these headers has to declare them in its `Access-Control-Allow-Headers`, which means deploying
 * that whole service in the API. These three are the only ones where the waiver has any effect (Reports
 * scope) and the only ones the concession supervisor view consumes.
 */
const VIEW_AS_ENDPOINT_PREFIXES = ["/sales", "/bills", "/summary"];

/** Value of `x-yoshi-view-as` the API accepts for each panel view */
const CONCESSION_SUPERVISOR_VIEW = "concession_supervisor";

/**
 * The view each ViewType declares. Only the concession view declares anything: simulating a venue admin is
 * precisely what must keep the concession hidden.
 * It is a Record<ViewType, ...> on purpose: a new ViewType does not compile until someone decides what it
 * declares.
 */
const VIEW_TYPE_DECLARED_VIEW: Record<ViewType, string | null> = {
  venue: null,
  concession: CONCESSION_SUPERVISOR_VIEW,
  consultation: null,
  pos: null,
  preparer: null,
  runner: null,
};

interface ViewAsState {
  viewType: ViewType;
  concessionUUID: string;
}

/**
 * State lives in a module variable, not in localStorage, and deliberately so.
 * `viewType` and the concession are persisted by effects (App.tsx and BusinessProvider) that run after the
 * effects of the screens that just mounted: reading them from storage would make the first requests after
 * pressing "Cambiar" go out with the previous view. Here it is written synchronously when the change is
 * applied.
 */
let objViewAsState: ViewAsState | null = null;

/**
 * Records the view the superadmin has just applied. Call it synchronously on the modal's submit, before
 * changing contexts or navigating.
 * @param viewType Selected view
 * @param concessionUUID UUID of the selected concession (empty when the view is not a concession one)
 */
export const setViewAsState = (viewType: ViewType, concessionUUID: string): void => {
  objViewAsState = { viewType, concessionUUID: concessionUUID || "" };
};

/**
 * View mode headers for the request being made, only for the endpoints on the allowlist.
 * Returns an empty object (fail-closed) unless the user is a superadmin, the effective view declares a
 * simulable view, and a concession is selected.
 * @param endpoint Endpoint about to be called, as the service built it
 * @returns Headers to inject, or `{}` when it does not apply
 */
export const getViewAsHeaders = (endpoint: string): Record<string, string> => {
  if (!VIEW_AS_ENDPOINT_PREFIXES.some((prefix) => endpoint.startsWith(prefix))) {
    return {};
  }

  const context = getContext();
  if (context?.user?.id_profile !== profiles.superAdmin) {
    return {};
  }

  // After a refresh the module variable is gone: fall back to the already rehydrated context
  const viewType: ViewType | undefined = objViewAsState?.viewType ?? context?.viewType;
  const concessionUUID: string =
    objViewAsState?.concessionUUID || getConcessionSelected()?.uuid || "";

  const declaredView: string | null = viewType ? VIEW_TYPE_DECLARED_VIEW[viewType] : null;
  if (!declaredView || !concessionUUID) {
    return {};
  }

  return {
    [VIEW_AS_HEADER]: declaredView,
    [VIEW_AS_CONCESSION_HEADER]: concessionUUID,
  };
};
